import {
  getPublicReaderPath,
  parseAppReaderQuery,
  type ReaderQuery,
} from '../packages/content/src/reader-routes'

/** The public website origin. Shared links must always point here, not to the app. */
export const PUBLIC_SITE_ORIGIN = 'https://hayirhah.com'

/** Query keys that belong to the `/app-reader` shell and have no meaning on the website. */
const APP_READER_QUERY_KEYS = ['kind', 'type', 'page']

/** Converts URL search params into the plain object shape the reader parser expects. */
const searchParamsToReaderQuery = (searchParams: URLSearchParams): ReaderQuery => {
  const query: ReaderQuery = {}
  searchParams.forEach((value, key) => {
    query[key] = value
  })
  return query
}

/**
 * Rewrites an `/app-reader?kind=...` URL to its public reader path (e.g. `/kuran/50`).
 * Returns `null` when the URL is not a valid app reader URL.
 */
const getPublicPathForAppReader = (url: URL): string | null => {
  if (url.pathname !== '/app-reader') return null

  const route = parseAppReaderQuery(searchParamsToReaderQuery(url.searchParams))
  if (!route) return null

  // Keep any extra params (e.g. `section`) but drop the app-only ones.
  const remainingParams = new URLSearchParams(url.searchParams)
  APP_READER_QUERY_KEYS.forEach((key) => remainingParams.delete(key))
  const search = remainingParams.toString()

  return `${getPublicReaderPath(route)}${search ? `?${search}` : ''}${url.hash}`
}

/**
 * Builds the public hayirhah.com URL for a page URL.
 * Inside the native app the page origin is the app's local origin, so we keep only
 * the path and attach the public website origin.
 */
export const getPublicShareUrl = (currentUrl: string): string => {
  const url = new URL(currentUrl)
  const publicPath =
    getPublicPathForAppReader(url) ?? `${url.pathname}${url.search}${url.hash}`
  return `${PUBLIC_SITE_ORIGIN}${publicPath}`
}
